import * as React from 'react';
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Avatar
} from '@mui/material';
import { usePermissions, useNotify } from 'react-admin';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import StarIcon from '@mui/icons-material/Star';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

const stats = [
    { title: 'Squadre', value: 18, icon: <GroupsIcon sx={{ color: '#4A90E2', fontSize: 36 }} />, color: '#E3F2FD' },
    { title: 'Partite', value: 42, icon: <SportsSoccerIcon sx={{ color: '#41ad72', fontSize: 36 }} />, color: '#E8F5E9' },
    { title: 'Goal', value: 123, icon: <EmojiEventsIcon sx={{ color: '#F5A623', fontSize: 36 }} />, color: '#FFF8E1' },
    { title: 'MVP', value: 7, icon: <StarIcon sx={{ color: '#FFD700', fontSize: 36 }} />, color: '#FFFDE7' }
];

// Configurazione delle colonne
const COLUMN_CONFIG = {
    posizione: {
        label: 'Pos.',
        key: 'posizione',
        align: 'left',
        default: true,
        sortable: true,
        width: 80,
        render: (value, row, index) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {(value || index + 1) === 1 && (
                    <EmojiEvents sx={{ color: '#FFD700', fontSize: 24 }} />
                )}
                {(value || index + 1) === 2 && (
                    <EmojiEvents sx={{ color: '#C0C0C0', fontSize: 22 }} />
                )}
                {(value || index + 1) === 3 && (
                    <EmojiEvents sx={{ color: '#CD7F32', fontSize: 20 }} />
                )}
                <Chip
                    label={value || index + 1}
                    size="small"
                    color={(value || index + 1) <= 3 ? 'success' : 'default'}
                    sx={{ fontWeight: 700 }}
                />
            </Box>
        )
    },
    squadra: {
        label: 'Squadra',
        key: 'squadra',
        align: 'left',
        default: true,
        sortable: true,
        width: 220,
        render: (value, row, index, teamsData) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                    src={teamsData?.get(row.teamId)?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(teamsData.get(row.teamId).foto)}` : undefined}
                    alt={value}
                    sx={{ width: 28, height: 28 }}
                >
                    {value?.charAt(0).toUpperCase() || '⚽'}
                </Avatar>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: index < 3 ? 600 : 400,
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                    component="a"
                    href={`#/squadre/${row.teamId}`}
                >
                    {value || `Squadra ${index + 1}`}
                </Typography>
            </Box>
        )
    },
    punti: {
        label: 'Punti',
        key: 'punti',
        align: 'center',
        default: true,
        sortable: true,
        width: 80,
        render: (value, row, index) => (
            <Chip
                label={value || 0}
                size="small"
                sx={{
                    bgcolor: index < 3 ? getPositionColor(index) : '#e0e0e0',
                    color: index < 3 ? '#fff' : '#666',
                    fontWeight: 600
                }}
            />
        )
    },
    partiteGiocate: {
        label: 'P.G.',
        key: 'partiteGiocate',
        align: 'center',
        default: true,
        sortable: true,
        width: 60,
        render: (value) => <Typography variant="body2">{value || 0}</Typography>
    },
    vinte: {
        label: 'V',
        key: 'vinte',
        align: 'center',
        default: false,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    pareggiate: {
        label: 'P',
        key: 'pareggiate',
        align: 'center',
        default: false,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    perse: {
        label: 'S',
        key: 'perse',
        align: 'center',
        default: false,
        sortable: true,
        width: 50,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600 }}>
                {value || 0}
            </Typography>
        )
    },
    record: {
        label: 'V-P-S',
        key: 'record',
        align: 'center',
        default: true,
        sortable: false,
        width: 80,
        render: (value, row) => (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {row.vinte || 0}-{row.pareggiate || 0}-{row.perse || 0}
            </Typography>
        )
    },
    goalFatti: {
        label: 'GF',
        key: 'goalFatti',
        align: 'center',
        default: false,
        sortable: true,
        width: 60,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#4caf50' }}>
                {value || 0}
            </Typography>
        )
    },
    goalSubiti: {
        label: 'GS',
        key: 'goalSubiti',
        align: 'center',
        default: false,
        sortable: true,
        width: 60,
        render: (value) => (
            <Typography variant="body2" sx={{ color: '#f44336' }}>
                {value || 0}
            </Typography>
        )
    },
    differenzaReti: {
        label: 'DR',
        key: 'differenzaReti',
        align: 'center',
        default: false,
        sortable: true,
        width: 60,
        render: (value) => {
            const diff = value || 0;
            const color = diff > 0 ? '#4caf50' : diff < 0 ? '#f44336' : '#757575';
            const icon = diff > 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> :
                diff < 0 ? <TrendingDownIcon sx={{ fontSize: 14 }} /> :
                    <RemoveIcon sx={{ fontSize: 14 }} />;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
                        {diff > 0 ? `+${diff}` : diff}
                    </Typography>
                    {React.cloneElement(icon, { sx: { ...icon.props.sx, color } })}
                </Box>
            );
        }
    },
    ultime5: {
        label: 'Ultime 5',
        key: 'ultime5',
        align: 'center',
        default: false,
        sortable: false,
        width: 100,
        render: (value) => {
            if (!value) return <Typography variant="body2">-</Typography>;
            return (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {value.split('').map((result, idx) => {
                        const color = result === 'V' ? '#4caf50' :
                            result === 'P' ? '#ff9800' :
                                result === 'S' ? '#f44336' : '#e0e0e0';
                        return (
                            <Chip
                                key={idx}
                                label={result}
                                size="small"
                                sx={{
                                    bgcolor: color,
                                    color: '#fff',
                                    fontSize: '0.6rem',
                                    height: 20,
                                    width: 20,
                                    minWidth: 20
                                }}
                            />
                        );
                    })}
                </Box>
            );
        }
    }
};

// Funzioni helper
const getPositionColor = (index) => {
    if (index === 0) return '#FFD700'; // Oro
    if (index === 1) return '#C0C0C0'; // Argento
    if (index === 2) return '#CD7F32'; // Bronzo
    return '#757575'; // Grigio per le altre posizioni
};

const getPositionIcon = (index) => {
    if (index < 3) {
        return <EmojiEventsIcon sx={{ color: getPositionColor(index), fontSize: 20 }} />;
    }
    return null;
};

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function Dashboard() {
    const { permissions } = usePermissions();
    const navigate = useNavigate();
    const notify = useNotify();
    const [classifica, setClassifica] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [visibleColumns, setVisibleColumns] = React.useState(() => {
        // Carica configurazione salvata o usa default
        const saved = localStorage.getItem('dashboard-visible-columns');
        if (saved) {
            return JSON.parse(saved);
        }
        // Configurazione di default
        return Object.keys(COLUMN_CONFIG).filter(key => COLUMN_CONFIG[key].default);
    });
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [sortConfig, setSortConfig] = React.useState({ key: 'punti', direction: 'desc' });
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [teamsData, setTeamsData] = React.useState(new Map());

    // Carica la classifica
    const loadClassifica = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/tbl-classifica-snapshot-generale`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        $limit: 20,
                        $sort: { posizione: 1 }
                    }
                }
            );

            let classificaData = response.data.data || [];

            // Applica ordinamento
            if (sortConfig.key && COLUMN_CONFIG[sortConfig.key]?.sortable) {
                classificaData = [...classificaData].sort((a, b) => {
                    const aVal = a[sortConfig.key] || 0;
                    const bVal = b[sortConfig.key] || 0;

                    if (sortConfig.direction === 'asc') {
                        return aVal - bVal;
                    } else {
                        return bVal - aVal;
                    }
                });
            }

            setClassifica(classificaData);

            // Carica dati team per stemmi
            const uniqueTeamIds = [...new Set(classificaData.map(c => c.teamId))];
            if (uniqueTeamIds.length > 0) {
                const teamsRes = await axios.get(`${host}/tbl-team`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        id: { $in: uniqueTeamIds },
                        $limit: uniqueTeamIds.length
                    }
                });
                const teamsMap = new Map();
                (teamsRes.data.data || []).forEach(team => {
                    teamsMap.set(team.id, team);
                });
                setTeamsData(teamsMap);
            }
        } catch (error) {
            console.error("Errore nel recupero della classifica:", error);
            setError("Errore nel caricamento della classifica");
            notify("Errore nel caricamento della classifica", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica i dati al mount del componente
    React.useEffect(() => {
        loadClassifica();
    }, [sortConfig]);

    // Salva configurazione colonne
    React.useEffect(() => {
        localStorage.setItem('dashboard-visible-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSort = (columnKey) => {
        if (!COLUMN_CONFIG[columnKey]?.sortable) return;

        setSortConfig(prev => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const resetToDefault = () => {
        setVisibleColumns(Object.keys(COLUMN_CONFIG).filter(key => COLUMN_CONFIG[key].default));
        setAnchorEl(null);
    };

    // Funzione per gestire il click sui pulsanti che devono mostrare il dialog
    const handleActionClick = (e) => {
        e.preventDefault();
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    Benvenuto nella Dashboard
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'grey.600', mb: 3 }}>
                    Ecco una panoramica aggiornata delle attività principali
                </Typography>

                {/* Statistiche principali */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {stats.map((item, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={item.title}>
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                            >
                                <Card
                                    sx={{
                                        bgcolor: item.color,
                                        borderRadius: 3,
                                        boxShadow: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 3
                                        }
                                    }}>
                                    <Box sx={{ mr: 2 }}>{item.icon}</Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">{item.title}</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{item.value}</Typography>
                                    </Box>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                {/* Sezione principale con azioni rapide e classifica */}
                <Grid container spacing={3}>
                    {/* Azioni rapide */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2, height: 'fit-content' }}>
                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                                Azioni rapide
                            </Typography>
                            {/* <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AddCircleIcon />}
                                sx={{
                                    mb: 2,
                                    bgcolor: '#41ad72',
                                    color: '#fff',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    py: 1.5,
                                    '&:hover': {
                                        bgcolor: '#369963'
                                    }
                                }}
                                onClick={handleActionClick}
                            >
                                Nuova Partita
                            </Button> */}
                            {/* <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<AddCircleIcon />}
                                sx={{
                                    mb: 2,
                                    color: '#4A90E2',
                                    borderColor: '#4A90E2',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: '#357ABD',
                                        color: '#357ABD',
                                        bgcolor: 'rgba(74, 144, 226, 0.04)'
                                    }
                                }}
                                onClick={handleActionClick}
                            >
                                Nuova Squadra
                            </Button> */}
                            <Button

                                fullWidth
                                variant="outlined"
                                startIcon={<AddCircleIcon />}
                                sx={{
                                    mb: 2,
                                    color: '#4A90E2',
                                    borderColor: '#4A90E2',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: '#357ABD',
                                        color: '#357ABD',
                                        bgcolor: 'rgba(74, 144, 226, 0.04)'
                                    }
                                }}
                                onClick={() => navigate('/squadre/create')}>
                                Nuova Squadra
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<EmojiEventsIcon />}
                                sx={{
                                    bgcolor: '#F5A623',
                                    color: '#fff',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    py: 1.5,
                                    '&:hover': {
                                        bgcolor: '#E8941F'
                                    }
                                }}
                                href="#/classifica"
                            >
                                Classifica Completa
                            </Button>
                        </Card>
                    </Grid>

                    {/* Classifica */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    🏆 Classifica Squadre
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Configura colonne">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => setAnchorEl(e.currentTarget)}
                                        >
                                            <Badge badgeContent={visibleColumns.length} color="primary">
                                                <SettingsIcon />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                    <Button
                                        size="small"
                                        onClick={loadClassifica}
                                        disabled={loading}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500
                                        }}
                                    >
                                        {loading ? <CircularProgress size={16} /> : 'Aggiorna'}
                                    </Button>
                                </Box>
                            </Box>

                            {/* Menu configurazione colonne */}
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                                PaperProps={{
                                    sx: { maxHeight: 400, width: 250 }
                                }}
                            >
                                <MenuItem onClick={resetToDefault}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Reset a default
                                    </Typography>
                                </MenuItem>
                                <MenuItem divider />
                                {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                                    <MenuItem key={key} onClick={() => handleColumnToggle(key)}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={visibleColumns.includes(key)}
                                                    size="small"
                                                />
                                            }
                                            label={config.label}
                                            sx={{ margin: 0 }}
                                        />
                                    </MenuItem>
                                ))}
                            </Menu>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {loading && !classifica.length ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : classifica.length > 0 ? (
                                <TableContainer sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                {visibleColumns.map(columnKey => {
                                                    const config = COLUMN_CONFIG[columnKey];
                                                    return (
                                                        <TableCell
                                                            key={columnKey}
                                                            align={config.align}
                                                            sx={{
                                                                fontWeight: 600,
                                                                width: config.width,
                                                                cursor: config.sortable ? 'pointer' : 'default',
                                                                bgcolor: sortConfig.key === columnKey ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                                '&:hover': config.sortable ? {
                                                                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                                } : {}
                                                            }}
                                                            onClick={() => handleSort(columnKey)}
                                                        >
                                                            {config.label}
                                                            {sortConfig.key === columnKey && (
                                                                <span style={{ marginLeft: 4 }}>
                                                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                                </span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {classifica.map((squadra, index) => (
                                                <TableRow
                                                    key={squadra.id || index}
                                                    sx={{
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                        },
                                                        bgcolor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'inherit'
                                                    }}
                                                >
                                                    {visibleColumns.map(columnKey => {
                                                        const config = COLUMN_CONFIG[columnKey];
                                                        const value = squadra[config.key];

                                                        return (
                                                            <TableCell
                                                                key={columnKey}
                                                                align={config.align}
                                                                sx={{ width: config.width }}
                                                            >
                                                                {config.render ?
                                                                    config.render(value, squadra, index, teamsData) :
                                                                    value || '-'
                                                                }
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        Nessuna squadra trovata nella classifica
                                    </Typography>
                                </Box>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </motion.div>

            {/* Dialog per funzionalità in arrivo */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <InfoIcon sx={{ fontSize: 48, color: '#4A90E2', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Funzionalità in Arrivo
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                        Attivo per il prossimo aggiornamento
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Questa funzionalità sarà disponibile nella prossima versione dell'applicazione.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        onClick={handleDialogClose}
                        variant="contained"
                        sx={{
                            bgcolor: '#4A90E2',
                            color: '#fff',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 4,
                            '&:hover': {
                                bgcolor: '#357ABD'
                            }
                        }}
                    >
                        Ho Capito
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Dashboard;